import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as EditIcon } from "../../assets/icons/edit.svg";
import { ReactComponent as TrashIcon } from "../../assets/icons/delete.svg";
import { ReactComponent as CrossIcon } from "../../assets/icons/cross.svg";
import { ReactComponent as MoreIcon } from "../../assets/icons/more.svg";
import { ReactComponent as ExportIcon } from '../../assets/icons/export.svg';
import { ReactComponent as FilterIcon } from '../../assets/icons/filter.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';

import { ReactComponent as DuplicateIcon } from '../../assets/icons/duplicate.svg';
import { handleExportCSV } from '../../utils/commonHandlers';
import SearchBar from '../SearchBar/SearchBar';
import Button from '../Button/Button';
import DropDown from '../DropDown/DropDown';
import './TableauDeBord.css';

// Ajouter cette interface pour les options d'action
interface ActionOption {
  label: string;
  onClick: (id: string) => void;
  icon?: React.ReactNode;
}

interface Column {
  field: string;
  header: string;
  renderCell?: (value: any, row?: any) => React.ReactNode;
  sortable?: boolean;
}

interface TableauDeBordProps {
  data: any[];
  columns: Column[];
  // searchPlaceholder?: string;
  typeFilters?: boolean;
  onSearch?: (query: string) => void;
  onExport?: () => void;
  // onFilter?: () => void;
  onRowClick?: (id: string) => void;
  isSelectable?: boolean;
  showActionBar?: boolean;
  onDuplicate?: (ids: string[]) => void;
  onDelete?: () => void;
  selectedItems: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  actionOptions?: ActionOption[];
  getActionOptions?: (row: any) => ActionOption[];
  showActionsColumn?: boolean;
  itemsPerPage?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  totalPages?: number;
  totalItems?: number;
  renderCell?: (column: string, row: any) => React.ReactNode;
  showTotalCount?: boolean;
  showPagination?: boolean;
  disableDefaultSort?: boolean; // Désactive le tri par défaut si les données sont déjà triées
}

const TableauDeBord: React.FC<TableauDeBordProps> = ({
  data = [],
  columns,
  // searchPlaceholder = "Rechercher...",
  typeFilters = false,
  showActionBar = false,
  onSearch,
  onExport,
  // onFilter,
  onRowClick,
  onDelete,
  onDuplicate,
  isSelectable = true,
  itemsPerPage = 50,
  actionOptions = [],
  getActionOptions,
  selectedItems = [],
  onSelectionChange = () => { },
  showActionsColumn = true,
  currentPage = 1,
  onPageChange = () => { },
  totalPages: totalPagesProp,
  totalItems: totalItemsProp,
  renderCell,
  showTotalCount = true,
  showPagination = true,
  disableDefaultSort = false, // Par défaut, le tri automatique est actif
}) => {
  // const [currentPage, setCurrentPage] = useState(1);
  // Utilisation directe des props pour la sélection
  // Nous n'avons plus besoin de gérer l'état du menu car nous utilisons des boutons directs

  // Modifier la fonction qui calcule les éléments de la page actuelle
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedData.slice(startIndex, endIndex);
  };

  // Initialize sort config to sort by date in descending order
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc' | null;
  }>({ key: 'created_at', direction: 'desc' });
  const navigate = useNavigate();

  const statusCounts = Array.isArray(data) ? data.reduce((acc: { [key: string]: number }, item) => {
    const status = item?.status || item?.statut;
    if (status) {
      acc[status] = (acc[status] || 0) + 1;
    }
    return acc;
  }, {}) : {};

  const typeOptions = ['Tous', 'BC', 'Sub'];

  const handleSearch = async (query: string) => {
    if (onSearch) {
      onSearch(query);
    }
  };

  const handleRowClick = (id: string, event: React.MouseEvent) => {
    // Vérifier si le clic provient d'un élément interactif comme un bouton ou une case à cocher
    if ((event.target as HTMLElement).tagName === 'INPUT' ||
      (event.target as HTMLElement).tagName === 'BUTTON' ||
      (event.target as HTMLElement).closest('button') ||
      (event.target as HTMLElement).closest('.actions')) {
      return; // Ne pas déclencher le clic de ligne si on clique sur un élément interactif
    }

    if (onRowClick) {
      onRowClick(id);
    }
  };

  // Calculer le nombre total de pages
  // Utiliser totalPagesProp si fourni (vient de l'API), sinon calculer
  const totalPages = totalPagesProp || Math.ceil((totalItemsProp || data.length) / itemsPerPage);

  // Générer les numéros de page pour la pagination
  const getPageNumbers = () => {
    const maxDisplayedPages = 7; // Nombre maximum de pages à afficher
    const pages = [];

    // Si nous avons moins de pages que le maximum à afficher
    if (totalPages <= maxDisplayedPages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Cas où nous avons beaucoup de pages
      const halfMax = Math.floor(maxDisplayedPages / 2);

      // Toujours afficher la première page
      pages.push(1);

      // Gérer les cas où nous sommes près du début ou de la fin
      if (currentPage <= halfMax + 1) {
        // Près du début
        for (let i = 2; i <= maxDisplayedPages - 1; i++) {
          pages.push(i);
        }
        pages.push(null); // Ellipsis
        pages.push(totalPages);
      } else if (currentPage >= totalPages - halfMax) {
        // Près de la fin
        pages.push(null); // Ellipsis
        for (let i = totalPages - (maxDisplayedPages - 2); i < totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Au milieu
        pages.push(null); // Ellipsis avant
        for (let i = currentPage - Math.floor((maxDisplayedPages - 4) / 2); i <= currentPage + Math.floor((maxDisplayedPages - 4) / 2); i++) {
          pages.push(i);
        }
        pages.push(null); // Ellipsis après
        pages.push(totalPages);
      }
    }

    // console.log("Generated Page Numbers:", pages);
    return pages;
  };

  const sortData = (items: any[]) => {
    return [...items].sort((a, b) => {
      if (!sortConfig.key) return 0;

      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Special handling for dates
      if (sortConfig.key === 'dateCreation' || sortConfig.key === 'created_at') {
        // Vérifier si les valeurs sont définies
        if (aValue === undefined && bValue === undefined) return 0;
        if (aValue === undefined) return sortConfig.direction === 'asc' ? -1 : 1;
        if (bValue === undefined) return sortConfig.direction === 'asc' ? 1 : -1;

        try {
          // Pour les dates au format ISO (comme created_at dans votre API)
          const dateA = new Date(aValue);
          const dateB = new Date(bValue);
          return sortConfig.direction === 'asc'
            ? dateA.getTime() - dateB.getTime()
            : dateB.getTime() - dateA.getTime();
        } catch (error) {
          // En cas d'erreur, utilisez une comparaison simple
          return sortConfig.direction === 'asc'
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      }

      // Regular sorting for other fields
      if (aValue === bValue) return 0;
      if (aValue === undefined) return sortConfig.direction === 'asc' ? -1 : 1;
      if (bValue === undefined) return sortConfig.direction === 'asc' ? 1 : -1;

      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  // Use useMemo to prevent unnecessary re-sorting and filtering
  const sortedData = useMemo(() => {
    // Si le tri par défaut est désactivé, retourner les données telles quelles
    if (disableDefaultSort) {
      return data;
    }
    // Sinon, appliquer le tri habituel
    return sortData(data);
  }, [data, sortConfig, disableDefaultSort]);

  const getSortIcon = (columnField: string) => {
    const isActive = sortConfig.key === columnField;
    const activeDirection = sortConfig.direction;

    return (
      <div className="sort-icons">
        <svg
          className={`sort-icon-up ${isActive && activeDirection === 'asc' ? 'active' : ''}`}
          xmlns="http://www.w3.org/2000/svg"
          width="10"
          height="6"
          viewBox="0 0 8 5"
          fill="none"
        >
          <path d="M7.28571 4.5L4.28571 1.5L1.28571 4.5" stroke="#C2C2C2" strokeLinecap="round" />
        </svg>
        <br />
        <svg
          className={`sort-icon-down ${isActive && activeDirection === 'desc' ? 'active' : ''}`}
          xmlns="http://www.w3.org/2000/svg"
          width="10"
          height="6"
          viewBox="0 0 8 5"
          fill="none"
        >
          <path d="M1.28571 0.5L4.28571 3.5L7.28571 0.5" stroke="#C2C2C2" strokeLinecap="round" />
        </svg>
      </div>
    );
  };

  // Nous n'avons plus besoin de ces gestionnaires car nous utilisons des boutons directs

  const getRowActionOptions = (row: any) => {
    if (getActionOptions) {
      return getActionOptions(row);
    }
    return actionOptions || [];
  };

  // Ajoutez cette fonction à votre composant
  const handleSort = (field: string) => {
    let direction: 'asc' | 'desc' | null = 'asc';
    if (sortConfig.key === field && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === field && sortConfig.direction === 'desc') {
      direction = null;
    }
    setSortConfig({ key: field, direction });
  };

  // console.log("TableauDeBord props:", {
  //   dataLength: data.length,
  //   currentPage,
  //   totalPages,
  //   itemsPerPage,
  //   totalItems
  // });

  return (
    <>
      <div className="tableau-container">
        <table className="tableau">
          <thead>
            <tr>
              {/* {isSelectable && (
                // <th>
                //   <input
                //     type="checkbox"
                //     onChange={(e) => {
                //       setSelectedItems(e.target.checked ? getCurrentPageItems().map(item => item.id) : []);
                //     }}
                //   />
                // </th>
              )} */}
              {columns.map((column, index) => (
                <th
                  key={index}
                  onClick={() => handleSort(column.field)}
                  className="sortable-header"
                >
                  {column.header}
                  <span className="sort-icon">{getSortIcon(column.field)}</span>
                </th>
              ))}
              {showActionsColumn && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, rowIndex) => (
              <tr
                key={`${row.id}-${rowIndex}`}
                onClick={(e) => handleRowClick(row.id, e)}
                style={{ cursor: 'pointer' }}
              >
                {/* {isSelectable && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(row.id)}
                      onChange={(e) => {
                        setSelectedItems(e.target.checked
                          ? [...selectedItems, row.id]
                          : selectedItems.filter(id => id !== row.id)
                        );
                      }}
                    />
                  </td>
                )} */}
                {columns.map((column, colIndex) => (
                  <td key={`${row.id}-${column.field}-${colIndex}`}>
                    {column.renderCell ? column.renderCell(row[column.field], row) : row[column.field]}
                  </td>
                ))}
                {showActionsColumn && (
                  <td className="actions">
                    <div className="actions-buttons-container">
                      {getRowActionOptions(row).map((option, idx) => (
                        <button
                          key={`action-${row.id}-${idx}`}
                          className="action-button-direct"
                          onClick={(e) => {
                            e.stopPropagation();
                            option.onClick(row.id);
                          }}
                          title={option.label}
                        >
                          {option.icon}
                        </button>
                      ))}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {showPagination && (
          <div className="pagination">
            {showTotalCount && (
              <span>
                {`${((currentPage - 1) * itemsPerPage) + 1}-${Math.min(currentPage * itemsPerPage, totalItemsProp || data.length)} sur ${totalItemsProp || data.length} résultats`}
              </span>
            )}
            <div className="pagination-controls">
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Précédent
              </button>

              {getPageNumbers().map((number, index) =>
                number === null ? (
                  <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                ) : (
                  <button
                    key={number}
                    className={currentPage === number ? 'active' : ''}
                    onClick={() => onPageChange(number)}
                  >
                    {number}
                  </button>
                )
              )}

              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === (totalPages || 1)}
              >
                Suivant
              </button>
            </div>
          </div>
        )}
        {showActionBar && selectedItems.length > 0 && (
          <div className="action-bar">
            <div className="action-bar-content">
              <span className="selected-count">
                {selectedItems.length} sélectionné
              </span>
              <div className="action-buttons">
                <button
                  className="action-button"
                  onClick={() => onDuplicate && onDuplicate(selectedItems)}
                >
                  <DuplicateIcon />
                  Dupliquer
                </button>
                <button
                  className="action-button"
                  onClick={() => onExport && onExport()}
                >
                  <ExportIcon />
                  Exporter
                </button>
              </div>
              <button
                className="close-button"
                onClick={() => onSelectionChange([])}
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TableauDeBord;